import * as zod from 'zod';

import { MotionKind } from '@sb/motion-planning';
import { TCPOptionOrAuto } from '@sb/motion-planning/TCPOffsetOption';
import { BlendConfig } from '@sb/motion-planning/Waypoint';
import { Conditional, Expression, Space } from '@sb/routine-runner/types';

import { SpeedProfile } from '../../speed-profile';

// Distance unit type for waypoint offsets
const DistanceUnit = zod.union([
  zod.literal('meter'),
  zod.literal('millimeter'),
  zod.literal('inch'),
]);

export default zod.object({
  target: zod.union([
    Space.Position,
    zod.object({
      positionListID: zod.string(),
      positionListIndex: zod.number().int().nonnegative().optional(),
      expression: Expression.optional(),
    }),
    zod.object({
      expression: Expression,
    }),
  ]),
  shouldMatchJointAngles: zod.boolean(),
  shouldMatchDynamicBasePosition: zod.boolean(),
  motionKind: MotionKind,
  stopHere: zod.boolean(),
  blendConfig: BlendConfig,
  useParentBlendConfig: zod.boolean(),
  // Since per-waypoint speeed profiles are not yet supported, these arguements are ignored.
  // I'm keeping them here for now to avoid breaking existing routines.
  // see https://github.com/standardbots/sb/pull/9257
  speedProfile: SpeedProfile,
  useParentSpeedProfile: zod.boolean(),
  // motion speed settings percentage for this specific waypoint - the per waypoint
  // speed profile is not yet fully supported, but we can do scaling based on this percentage
  // coming from the waypoint settings UI
  motionSpeedPercent: zod.number().min(1).max(100).nullable().optional(),
  // TCP offset option for this specific waypoint
  tcpOption: TCPOptionOrAuto,
  // Optional gripper ID to get TCP offsets from (can be disabled)
  gripperID: zod.string().optional(),
  // Distance unit for offset values
  distanceUnit: DistanceUnit.default('meter'),
  relativeConfig: zod.object({
    targetKind: zod
      .union([
        zod.literal('singlePosition'),
        zod.literal('positionList'),
        zod.literal('expression'),
      ])
      .default('singlePosition'),
    frame: zod.string().default('base'),
    offsetX: Expression.optional(),
    offsetY: Expression.optional(),
    offsetZ: Expression.optional(),
    offsetRx: Expression.optional(),
    offsetRy: Expression.optional(),
    offsetRz: Expression.optional(),
  }),
  palletConfig: zod
    .object({
      selectedLayer: Expression.optional(),
      selectedBox: Expression.optional(),
      selectedBoxType: zod.string().optional(),
      selectedPalletBaseID: zod.string().optional(),
      moveType: zod
        .union([
          zod.literal('box'),
          zod.literal('approach'),
          zod.literal('pickup'),
        ])
        .default('box'),
    })
    .default({
      selectedLayer: undefined,
      selectedBox: undefined,
      selectedBoxType: undefined,
      selectedPalletBaseID: undefined,
      moveType: 'box',
    }),
  stopEarlyConditions: Conditional.nullable().default(null),
  useDeterministicPlanner: zod.boolean().optional(),
});
